import { useQuery } from '@tanstack/react-query';
import { PlayIcon } from 'lucide-preact';
import { useState } from 'preact/hooks';

import { Badge } from '@/components/badges';
import { Button } from '@/components/Buttons';
import { useCoveringModal } from '@/components/SpeedyFlow/coveringModal';
import { getProductsQueryOptions } from '@/queries/products';
import { useSelector } from '@/redux/helper';
import { useCurrentStreamEvent } from '@/routes/habitat/hooks';
import { trackMP } from '@/shared/mp';

import ProductCard, { type CardFace, faceComponentName } from './ProductCard';
import ProductEligibilityResult from './ProductEligibilityResult';
import ProductPrice from './ProductPrice';
import TrailerModal from './TrailerModal';
import useEligibilityBlocked from './useEligibilityBlocked';
import useProductCheckout from './useProductCheckout';

interface SingleHabitatDayPassProps {
  habitatId: string;
  entryPoint?: string;
}

const LOOKUP_KEY = 'single-habitat-day-pass';

const WHATS_INCLUDED_IMAGE = 'https://zoolife.tv/assets/products/whats-included-l1a.webp';

const SingleHabitatDayPass = ({ habitatId, entryPoint }: SingleHabitatDayPassProps) => {
  const habitat = useSelector((state) => state.allHabitats.find((habitat) => habitat._id === habitatId));
  // Offer id carried in from the `ppto` param (already lowercased); redeemed as the checkout `offer`.
  // Snapshotted by the flow when it opened, because the URL it came from does not survive the
  // journey. Undefined when this card is rendered outside a flow, which is the honest answer: there
  // was no link asking for an offer.
  const offerId = useSelector((state) => state.modals.speedyFlow?.offerId);
  const { data: product } = useQuery({
    ...getProductsQueryOptions({
      habitatId,
      lookupKey: LOOKUP_KEY,
    }),
    select: (data) => data.list[0],
  });
  const eligibilityBlocked = useEligibilityBlocked(product);
  const { start: startCheckout, isPending } = useProductCheckout();

  const [schedule, now] = useCurrentStreamEvent(habitat?._id);
  const [showTrailer, setShowTrailer] = useState(false);

  // Holds the flow open while the trailer is over it, so a click on the trailer's backdrop closes
  // the trailer and nothing else. No-op when this card is rendered outside the flow.
  useCoveringModal(showTrailer);

  if (!habitat) return null;

  // `online` is the computed liveness (a camera reporting on); `isStreamOn` is a separate
  // persisted flag and is not the same thing. Reading the latter marked genuinely live habitats
  // "Unavailable" — and did so mostly for signed-out visitors, whose habitat list does not carry
  // it, which made an availability bug look like an eligibility one.
  const isOnline = habitat.online ?? habitat.isStreamOn;
  const isEndOfDay =
    isOnline && schedule && Date.parse(schedule.stopTime) < now + 3_600_000 && now > Date.parse(schedule.startTime);
  const isAsleep = !isOnline && !!schedule;
  const isOffline = !isOnline && !schedule;

  // No trailer, no button — in either face. `HabitatIcon` does the same. A disabled "View Trailer"
  // advertises something that does not exist, and most habitats have one, so the gap is rare.
  const { trailer } = habitat;

  const trackedProps = { entryPoint, productLookupKey: LOOKUP_KEY, habitatId };

  /**
   * Both faces render these, and both faces are mounted at once — the panel is hidden with
   * `aria-hidden`, not unmounted, so it can be measured. That means every `data-component` here
   * would otherwise appear twice in the DOM and turn each existing e2e selector into an ambiguous
   * match, so the panel's copies are named for the face they are on.
   */
  const renderViewTrailerButton = (face: CardFace) =>
    trailer ? (
      <Button
        data-component={faceComponentName(face, 'view-trailer-button')}
        variant="outline"
        size="lg"
        className="w-full"
        onClick={() => {
          trackMP('click-view-trailer', trackedProps);
          setShowTrailer(true);
        }}
      >
        <PlayIcon className="size-[18px]" />
        View Trailer
      </Button>
    ) : null;

  const renderCheckoutButton = (face: CardFace) => (
    <Button
      data-component={faceComponentName(face, 'checkout-button')}
      disabled={!product || isOffline || isPending || eligibilityBlocked}
      loading={isPending}
      size="lg"
      className="w-full"
      onClick={() => {
        if (!product) return;
        startCheckout({
          productId: product._id,
          priceId: product.price.priceId,
          // Generic product: the habitat it unlocks is attached here, per checkout.
          associatedHabitatIds: [habitat._id],
          successPath: `/h/${habitat.zoo.slug}/${habitat.slug}`,
          cancelPath: `/h/${habitat.zoo.slug}/${habitat.slug}`,
          offerId,
          productLookupKey: product.productLookupKey,
          entryPoint,
        });
      }}
    >
      {/*
        NOTE: the "Get it for free" label keys off offerId alone, not real offer eligibility, so a
        user who already redeemed `explorer` still sees it and then hits a 400 "Offer not available"
        on checkout. Accepted for now (simpler than threading eligibility through the product query);
        revisit if the dead-end checkout proves confusing — surface eligibility and fall back to
        "Select Pass" / disable when the offer is spent.
      */}
      {isOffline ? 'Unavailable' : offerId === 'explorer' ? 'Get it for free' : 'Select Pass'}
    </Button>
  );

  return (
    <>
      <ProductCard.Root data-component="single-habitat-day-pass">
        <ProductCard.Front className="justify-center gap-3">
          <div className="relative h-[104px] w-full rounded-t-2xl bg-gray-400">
            {habitat.backgroundImage && (
              <img
                src={habitat.backgroundImage}
                alt={habitat.title}
                className="preflight preflight-img size-full overflow-hidden rounded-t-2xl object-cover"
              />
            )}
            {isOnline && !isEndOfDay && (
              <Badge variant="moss" shadow="small" className="absolute right-3 top-2 gap-[3px] uppercase">
                <span className="size-[5px] rounded-full bg-rose-500" />
                LIVE
              </Badge>
            )}
            <div className="absolute inset-x-0 -bottom-7 flex items-center justify-center">
              <div className="relative">
                {habitat.profileImage ? (
                  <img
                    src={habitat.profileImage}
                    alt={habitat.title}
                    className="preflight preflight-img size-[108px] overflow-hidden rounded-full border border-white object-cover"
                  />
                ) : (
                  <div
                    data-available={!isOffline}
                    className="preflight flex size-[108px] items-center justify-center overflow-hidden rounded-full border border-white bg-gray-400 text-2xl font-bold text-white"
                  >
                    {habitat.title.charAt(0)}
                  </div>
                )}
                {isOffline && <div className="absolute inset-0 rounded-full bg-white/50" />}
                {habitat.zoo && (
                  <div className="absolute bottom-0 right-0 rounded-full bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
                    <img
                      src={habitat.zoo.logo}
                      alt={habitat.zoo.name}
                      className="preflight preflight-img size-[44px] overflow-hidden rounded-full object-contain"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <h4 className="preflight preflight-h4 mx-4 max-w-[304px] pt-7 text-center text-2xl/7 font-bold text-green-900">
            Single Day Pass
          </h4>
          {isEndOfDay ? (
            <span className="flex rounded-md bg-rose-700 px-2 py-1 text-[10px]/none font-bold text-white">End of Day</span>
          ) : isAsleep ? (
            <span className="flex rounded-md bg-offline-grey px-2 py-1 text-[10px]/none font-bold text-white">Asleep</span>
          ) : isOffline ? (
            <span className="flex rounded-md bg-offline-grey px-2 py-1 text-[10px]/none font-bold text-white">Offline</span>
          ) : null}
          <ProductPrice currency={product?.price.currency} price={product?.price.price} className="px-4" />
          <p className="preflight preflight-p mx-4 max-w-[286px] text-center text-base/6 text-stone-700">
            24h Access to <strong>{habitat.title}</strong> livestream.
          </p>
          <p className="preflight preflight-p mx-4 max-w-[286px] text-center text-sm/5 text-stone-500">
            Plus see what they were up to yesterday, last week or even last year!
          </p>
          <div className="flex w-full flex-col items-center gap-3 px-4 pb-4">
            {renderViewTrailerButton('front')}
            {renderCheckoutButton('front')}
            {product && <ProductEligibilityResult result={product.eligibility} />}
            <ProductCard.WhatsIncludedTrigger onOpen={() => trackMP('click-whats-included', trackedProps)} />
          </div>
        </ProductCard.Front>

        <ProductCard.WhatsIncluded>
          <ProductCard.BackButton />
          <ProductCard.WhatsIncludedHeading>
            Get a sneak peek into what the animals do when you&apos;re not at the zoo
          </ProductCard.WhatsIncludedHeading>
          <ProductCard.WhatsIncludedImage src={WHATS_INCLUDED_IMAGE} alt="A keeper holding a koala" />
          <ProductCard.Benefits product={product} />
          {renderViewTrailerButton('whats-included')}
          <ProductCard.CheckoutGroup>
            {renderCheckoutButton('whats-included')}
            {product && <ProductEligibilityResult result={product.eligibility} />}
          </ProductCard.CheckoutGroup>
        </ProductCard.WhatsIncluded>
      </ProductCard.Root>

      {showTrailer && trailer && <TrailerModal trailer={trailer} onClose={() => setShowTrailer(false)} />}
    </>
  );
};

export default SingleHabitatDayPass;
